import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ChevronRight, Calendar, MapPin, Share2, Heart, CheckCircle2 } from "lucide-react";
import { SiteShell } from "@/project/components/site-shell";
import { Badge } from "@/project/components/cards";
import { stories, getStory, getInstitution, IMG } from "@/project/data/sample";

export const Route = createFileRoute("/stories/$id")({
  loader: ({ params }: { params: any }) => {
    const s = getStory(params.id);
    if (!s) throw notFound();
    return s;
  },
  head: ({ loaderData }: { loaderData?: any }) => ({
    meta: [
      { title: `${loaderData?.title ?? "Story"} — Animal Seva` },
      { name: "description", content: loaderData?.excerpt ?? "" },
      { property: "og:image", content: loaderData?.image ?? "" },
    ],
  }),
  component: StoryDetail,
  notFoundComponent: () => (
    <SiteShell>
      <div className="mx-auto max-w-2xl py-24 px-6 text-center">
        <h1 className="font-display text-2xl">Story not found</h1>
        <Link to="/stories" className="mt-4 inline-block text-[var(--maroon)] underline">Back to stories</Link>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ reset }: { reset: any }) => (
    <SiteShell>
      <div className="mx-auto max-w-2xl py-24 px-6 text-center">
        <p>Something went wrong.</p>
        <button onClick={reset} className="mt-4 text-[var(--maroon)] underline">Try again</button>
      </div>
    </SiteShell>
  ),
});

function StoryDetail() {
  const s = Route.useLoaderData();
  const inst = getInstitution(s.institutionId);
  const related = stories.filter((x) => x.id !== s.id).slice(0, 3);
  const gallery = [s.image, IMG.heroCow, IMG.heroDog, IMG.heroBird];
  const timeline = [
    { date: "Day 1", title: "Rescue", body: "Our team responded to an emergency call within 40 minutes." },
    { date: "Day 3", title: "First surgery", body: "Stabilised, X-rayed and operated by the in-house vet team." },
    { date: "Week 4", title: "Physiotherapy begins", body: "Daily exercises, supportive diet, and gradual mobility training." },
    { date: "Month 4", title: "Full recovery", body: "Released into the campus pasture. Adopted by a regular donor family." },
  ];

  return (
    <SiteShell>
      <div className="border-b border-border/60 bg-[var(--cream-2)]">
        <div className="mx-auto flex max-w-7xl items-center gap-1.5 px-4 py-3 text-xs text-foreground/60 sm:px-6">
          <Link to="/" className="hover:text-[var(--maroon)]">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/stories" className="hover:text-[var(--maroon)]">Stories</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground/80 truncate">{s.title}</span>
        </div>
      </div>

      <article className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        <header className="text-center">
          <Badge tone="featured">Impact Story</Badge>
          <h1 className="mt-4 font-display text-4xl font-semibold text-[var(--maroon-deep)] sm:text-5xl">{s.title}</h1>
          <p className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm text-foreground/65">
            <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" />{new Date(s.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
            {inst && <Link to="/institutions/$id" params={{ id: inst.id }} className="inline-flex items-center gap-1 text-[var(--maroon)] hover:underline"><MapPin className="h-4 w-4" />{inst.name}</Link>}
          </p>
        </header>

        <div className="mt-10 overflow-hidden rounded-3xl shadow-[var(--shadow-card)]">
          <img src={s.image} alt={s.title} className="aspect-[16/9] w-full object-cover" />
        </div>

        <p className="mt-10 text-lg italic text-foreground/75 border-l-4 border-[var(--saffron)] pl-5">"{s.excerpt}"</p>

        <div className="mt-8 space-y-6 text-base leading-relaxed text-foreground/85">
          <p>{s.body}</p>
          <p>
            Every recovery story is the result of compassionate donors, dedicated caretakers, and the unbreakable
            spirit of the animals themselves. Behind each photo is a chain of small, deliberate actions — a rescue
            call answered, a surgery funded, a bandage changed, a meal served on time.
          </p>
          <p>
            This story would not exist without donors like you. Thank you for believing in transparent giving and
            for trusting verified institutions on Animal Seva.
          </p>
        </div>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-semibold text-[var(--maroon-deep)]">The journey</h2>
          <ol className="mt-6 relative space-y-6 border-l-2 border-[var(--cream-2)] pl-6">
            {timeline.map((t, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[31px] top-1 grid h-5 w-5 place-items-center rounded-full bg-[var(--maroon)] text-white">
                  <CheckCircle2 className="h-3 w-3" />
                </span>
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--saffron-deep)]">{t.date}</p>
                <p className="mt-0.5 font-display text-lg font-semibold">{t.title}</p>
                <p className="mt-1 text-sm text-foreground/70">{t.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-semibold text-[var(--maroon-deep)]">Photo gallery</h2>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {gallery.map((src, i) => (
              <div key={i} className="overflow-hidden rounded-xl shadow-[var(--shadow-soft)]">
                <img src={src} alt="" loading="lazy" className="aspect-square h-full w-full object-cover transition hover:scale-105" />
              </div>
            ))}
          </div>
        </section>

        {inst && (
          <section className="mt-12 rounded-3xl bg-[var(--maroon)] p-8 text-[var(--cream)] sm:p-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--saffron)]">Support this institution</p>
            <h2 className="mt-2 font-display text-2xl font-semibold sm:text-3xl">Help {inst.name} save more lives</h2>
            <p className="mt-3 text-[var(--cream)]/85">Your donation funds the next rescue, surgery, or shelter expansion.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/donate" search={{ institution: inst.id }} className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-[var(--maroon)] hover:bg-[var(--cream-2)]">
                Donate Now
              </Link>
              <Link to="/institutions/$id" params={{ id: inst.id }} className="rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10">
                Visit institution
              </Link>
            </div>
          </section>
        )}

        <div className="mt-10 flex justify-center gap-3">
          <button className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-medium hover:bg-[var(--cream-2)]">
            <Heart className="h-4 w-4 text-[var(--maroon)]" /> Save story
          </button>
          <button className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-medium hover:bg-[var(--cream-2)]">
            <Share2 className="h-4 w-4 text-[var(--maroon)]" /> Share
          </button>
        </div>
      </article>

      <section className="border-t border-border/60 bg-[var(--cream-2)] py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <h2 className="font-display text-2xl font-semibold text-[var(--maroon-deep)] sm:text-3xl">More stories</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {related.map((r) => (
              <Link key={r.id} to="/stories/$id" params={{ id: r.id }} className="group overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)]">
                <img src={r.image} alt="" loading="lazy" className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-105" />
                <div className="p-5">
                  <p className="text-xs text-foreground/70">{new Date(r.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                  <h3 className="mt-1 font-display text-lg font-semibold leading-snug">{r.title}</h3>
                  <p className="mt-2 text-sm text-foreground/70 line-clamp-2">{r.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
